// Tablas de plantillas PDF del motor react-pdf (solo definicion: las
// funciones viven en queries.ts / mutations.ts para no arrastrar
// `lib/functions` al grafo de `schema.ts`).
//
//   pdf_templates          plantillas por empresa y destino (tabla del sistema
//                          o coleccion propia); una puede ser la por defecto
//   pdf_template_versions  historial inmutable: una fila por guardado, para
//                          ver cambios y restaurar
//
// Permisos: modulo `custom_fields` (Configuracion -> Colecciones). Renderizar
// no lee estas funciones: las acciones usan `resolveTemplateForRender`.

import { v, type Infer } from "convex/values";
import { defineTableWithInternalControl, internalControl } from "../../lib/shared/validators";

/** Tablas del sistema que pueden tener plantillas propias. */
export const PDF_SYSTEM_TARGETS = [
  "invoices",
  "quotations",
  "sales",
  "credit_notes",
  "debit_notes",
  "purchase_orders",
  "work_orders",
  "stock_transfers",
  "inventory_counts",
] as const;
export type PdfSystemTarget = (typeof PDF_SYSTEM_TARGETS)[number];

export const pdfTemplateScope = v.union(v.literal("system_table"), v.literal("collection"));

export const validatorPdfTemplate = v.object({
  scope: pdfTemplateScope,
  /** Tabla del sistema ("invoices") o id de la coleccion (como string). */
  target: v.string(),
  collectionId: v.optional(v.id("collections")),
  name: v.string(),
  /** `PdfSchema` validado con `assertPdfSchema` al guardar. */
  schema: v.any(),
  isDefault: v.boolean(),
  enabled: v.boolean(),
  /** Numero de la ultima version guardada en `pdf_template_versions`. */
  version: v.number(),
  internalControl,
});
export type PdfTemplateDoc = Infer<typeof validatorPdfTemplate>;

export const pdf_templates = defineTableWithInternalControl(validatorPdfTemplate)
  .index("by_company_and_target", ["internalControl.companyId", "target"])
  .index("by_company_and_target_and_default", ["internalControl.companyId", "target", "isDefault"]);

export const validatorPdfTemplateVersion = v.object({
  templateId: v.id("pdf_templates"),
  version: v.number(),
  name: v.string(),
  schema: v.any(),
  /** "create" | "update" | "restore" | "clone". */
  reason: v.string(),
  internalControl,
});

export const pdf_template_versions = defineTableWithInternalControl(validatorPdfTemplateVersion).index(
  "by_template_and_version",
  ["templateId", "version"],
);

/**
 * Biblioteca de imagenes de las plantillas (logos propios, membretes,
 * sellos). Las plantillas las referencian como `asset:<storageId>`; solo
 * PNG/JPEG (lo que react-pdf dibuja: el editor convierte el resto antes de
 * subir).
 */
export const validatorPdfTemplateAsset = v.object({
  storageId: v.id("_storage"),
  name: v.string(),
  contentType: v.string(),
  size: v.number(),
  width: v.optional(v.number()),
  height: v.optional(v.number()),
  internalControl,
});

export const pdf_template_assets = defineTableWithInternalControl(validatorPdfTemplateAsset).index(
  "by_company_and_storage",
  ["internalControl.companyId", "storageId"],
);
